import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import HeyflowBanner from "@/components/HeyflowBanner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Shield, 
  Heart, 
  Home, 
  Users,
  Calculator,
  Phone,
  CheckCircle,
  Star,
  ArrowRight
} from "lucide-react";

// Story images
import storyRenateKlaus from "@/assets/story-renate-klaus.jpg";
import storyMarianneGerd from "@/assets/story-marianne-gerd.jpg";
import storySabineThomas from "@/assets/story-sabine-thomas.jpg";
import storyHannelorePeter from "@/assets/story-hannelore-peter.jpg";

const Erfolgsgeschichten = () => {
  const faqs = [
    {
      question: "Mindert ein Wohnrecht den Immobilienwert?",
      answer: "Ja, der Wert des Wohnrechts wird anhand der statistischen Lebenserwartung und der fiktiven Miete berechnet und vom Kaufpreis abgezogen."
    },
    {
      question: "Wer zahlt Instandhaltungen beim Nießbrauch?",
      answer: "Das ist Verhandlungssache. Üblicherweise übernimmt der 'wirtschaftliche Eigentümer' (Nießbraucher) die gewöhnliche Unterhaltung, während der Käufer für 'Dach und Fach' (große Reparaturen) zuständig sein kann."
    },
    {
      question: "Ist die Leibrente steuerpflichtig?",
      answer: "Ja, aber nur mit dem sogenannten Ertragsanteil, der vom Alter bei Rentenbeginn abhängt. Wir beraten Sie hierzu gerne."
    },
    {
      question: "Kann ich beim Rückmietverkauf gekündigt werden?",
      answer: "Nein, es werden spezielle Mietverträge mit Kündigungsverzicht (oft lebenslang) geschlossen. Sie sind vor Eigenbedarfskündigungen geschützt."
    },
    {
      question: "Was ist der Unterschied zwischen Leibrente und Zeitrente?",
      answer: "Die Leibrente wird lebenslang gezahlt – eine Wette auf ein langes Leben mit maximaler Sicherheit. Die Zeitrente bietet höhere Zahlungen über einen festen Zeitraum (z.B. 10 oder 15 Jahre) und ist vererbbar."
    },
    {
      question: "Was passiert mit meiner Immobilie nach meinem Tod?",
      answer: "Das hängt vom gewählten Modell ab. Bei der Leibrente geht die Immobilie an den Käufer. Beim Teilverkauf können die Erben den verkauften Anteil zurückkaufen. Bei der Seniorenfinanzierung können die Erben den Kredit ablösen und Eigentümer bleiben."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="min-h-screen">
        {/* Breadcrumb */}
        <nav className="container-comfortable py-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">Startseite</Link>
          <span className="mx-2">/</span>
          <span className="text-primary">Erfolgsgeschichten</span>
        </nav>
        
        <Helmet>
          <title>Immobilienverrentung Vergleich: Leibrente, Nießbrauch & Verkauf | renteplusimmobilie</title>
          <meta 
            name="description" 
            content="4 Paare, 4 Lösungen. Erfahren Sie anhand echter Geschichten, welches Modell der Immobilienverrentung (Leibrente, Nießbrauch, Rückmietverkauf) zu Ihnen passt. Jetzt kostenlos beraten lassen!" 
          />
          <meta name="keywords" content="Immobilienverrentung, Hausverkauf gegen Rente, Nießbrauchrecht berechnen, Rückmietverkauf Erfahrung, Leibrente vs Zeitrente, Kapitalbeschaffung Rentner" />
          <link rel="canonical" href="https://eigenheimverrentung.de/erfolgsgeschichten" />
          <meta name="robots" content="index, follow" />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="article" />
          <meta property="og:title" content="4 Paare, 4 Lösungen zur Immobilienverrentung" />
          <meta property="og:description" content="Erfahren Sie anhand echter Geschichten, welches Modell der Immobilienverrentung zu Ihnen passt." />
          <meta property="og:url" content="https://eigenheimverrentung.de/erfolgsgeschichten" />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Immobilienverrentung: 4 Erfolgsgeschichten" />
          <meta name="twitter:description" content="Leibrente, Nießbrauch, Rückmietverkauf – welches Modell passt zu Ihnen?" />
          
          {/* FAQ Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })}
          </script>

          {/* BreadcrumbList Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Startseite",
                  "item": "https://eigenheimverrentung.de/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Erfolgsgeschichten",
                  "item": "https://eigenheimverrentung.de/erfolgsgeschichten"
                }
              ]
            })}
          </script>

          {/* Review/Rating Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "RentePlusImmobilie - Eigenheimverrentung",
              "url": "https://eigenheimverrentung.de",
              "telephone": "+49-89-628246446",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "bestRating": "5",
                "worstRating": "1",
                "ratingCount": "4",
                "reviewCount": "4"
              },
              "review": [
                {
                  "@type": "Review",
                  "author": { "@type": "Person", "name": "Renate" },
                  "datePublished": "2025-09-15",
                  "reviewBody": "Seit die Rente monatlich kommt, schlafen wir wieder ruhig. Die neue Heizung ist bezahlt und wir planen die erste Kreuzfahrt unseres Lebens.",
                  "name": "Leibrente mit Mindestlaufzeitgarantie",
                  "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
                },
                {
                  "@type": "Review",
                  "author": { "@type": "Person", "name": "Marianne" },
                  "datePublished": "2025-07-20",
                  "reviewBody": "Der Nießbrauch war die beste Entscheidung. Wir haben die Kinder unterstützt, das Haus umgebaut – und die Einliegerwohnung bringt uns jetzt zusätzliche Mieteinnahmen.",
                  "name": "Nießbrauch mit Einliegerwohnung",
                  "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
                },
                {
                  "@type": "Review",
                  "author": { "@type": "Person", "name": "Sabine" },
                  "datePublished": "2025-11-10",
                  "reviewBody": "Der Rückmietverkauf hat uns den Neuanfang ermöglicht, den wir brauchten. Wir wohnen weiter hier, aber ohne die Last der Verantwortung.",
                  "name": "Rückmietverkauf mit Kündigungsschutz",
                  "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
                },
                {
                  "@type": "Review",
                  "author": { "@type": "Person", "name": "Hannelore" },
                  "datePublished": "2026-01-05",
                  "reviewBody": "Wir haben 50% unseres Hauses verkauft, leben weiter darin und haben endlich genug für den Alltag und unsere Pflege-Rücklagen.",
                  "name": "Teilverkauf 50% der Immobilie",
                  "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
                }
              ]
            })}
          </script>
        </Helmet>

        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
          <div className="container-comfortable relative">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6 text-base md:text-lg px-4 md:px-6 py-2">
                <Star className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                Über 10.000 beratene Eigentümer
              </Badge>
              
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 text-balance leading-tight">
                Ein Leben lang gearbeitet. Ein eigenes Haus gebaut. Und trotzdem fehlt am Monatsende das Geld?
              </h1>
              
              <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 text-balance max-w-3xl mx-auto">
                Nutzen Sie den Wert Ihrer Immobilie, ohne ausziehen zu müssen. Entdecken Sie die 4 Wege zur finanziellen Freiheit im Alter.
              </p>

              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="text-lg md:text-xl px-8 md:px-12 py-5 md:py-6"
                    onClick={() => window.location.href = '/#advisor-wizard'}
                  >
                    <Calculator className="mr-2 h-5 w-5 md:h-6 md:w-6" />
                    Kostenlose Berechnung anfordern
                  </Button>
                </div>

                <button 
                  className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors cursor-pointer whitespace-nowrap text-base md:text-lg font-medium"
                  onClick={() => window.open('tel:089628246446', '_self')}
                >
                  <Phone className="h-4 w-4 md:h-5 md:w-5" />
                  <span>089 62 82 46 46</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Einleitung Section */}
        <section className="py-12 md:py-20 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-6 md:mb-8">
                Das stille Problem vieler Eigentümer
              </h2>
              
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-4 md:space-y-6">
                <p className="text-lg md:text-xl leading-relaxed">
                  Der Ruhestand sollte die schönste Zeit des Lebens sein.
                </p>
                
                <p className="text-base md:text-lg leading-relaxed">
                  Sie haben Jahrzehnte hart gearbeitet. Sie haben gespart, verzichtet und Stein auf Stein gesetzt, um sich Ihren Traum vom Eigenheim zu erfüllen. Ihr Haus ist mehr als nur Steine – es ist Ihr Zuhause, Ihr Speicher an Erinnerungen, Ihr ganzer Stolz.
                </p>
                
                <p className="text-base md:text-lg leading-relaxed">
                  Doch die Realität sieht für viele anders aus: Die Rente fällt geringer aus als erhofft. Die Lebenshaltungskosten steigen. Das Dach müsste repariert werden, oder vielleicht träumen Sie von einer Reise, die Sie sich bisher nie gegönnt haben.
                </p>
                
                <div className="bg-accent/10 p-4 md:p-6 rounded-xl border-l-4 border-accent my-6 md:my-8">
                  <p className="text-lg md:text-xl font-semibold text-primary">
                    Sie sind „steinreich", aber „bargeldarm".
                  </p>
                </div>
                
                <p className="text-base md:text-lg leading-relaxed">
                  Viele Senioren glauben, die einzige Lösung sei der Verkauf und der schmerzhafte Auszug. <strong className="text-primary">Das ist falsch.</strong>
                </p>
                
                <p className="text-base md:text-lg leading-relaxed font-medium text-primary">
                  Lernen Sie vier Paare kennen. Paare wie Sie. Sie standen an genau demselben Punkt – und haben unterschiedliche Wege gefunden, ihr Kapital freizusetzen, ohne ihr Zuhause zu verlieren.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Story 1: Die Sicherheits-Optimierer */}
        <section className="py-12 md:py-20">
          <div className="container-comfortable">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-accent/10 p-3 rounded-xl">
                  <Shield className="h-6 w-6 md:h-8 md:w-8 text-accent" />
                </div>
                <Badge variant="outline" className="text-sm md:text-base">Leibrente vs. Zeitrente</Badge>
              </div>
              
              <Card className="card-elevated overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-5 gap-0">
                    <div className="lg:col-span-2 relative overflow-hidden">
                      <img 
                        src={storyRenateKlaus} 
                        alt="Renate und Klaus - glückliches Seniorenpaar im Garten" 
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/40 to-transparent" />
                      <div className="relative p-6 md:p-8 flex flex-col justify-end h-full min-h-[280px]">
                        <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3 md:mb-4">
                          Renate (78) und Klaus (80)
                        </h3>
                        <p className="text-lg md:text-xl text-accent font-semibold mb-4">
                          „Wir wollen keine Sorgen mehr, nur noch Sicherheit."
                        </p>
                        <p className="text-sm md:text-base text-white/80">
                          Die „Sicherheits-Optimierer"
                        </p>
                      </div>
                    </div>
                    
                    <div className="lg:col-span-3 p-6 md:p-8 space-y-4 md:space-y-6">
                      <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                        Renate war 40 Jahre lang Krankenschwester, Klaus arbeitete im Handwerk. Ihr kleines Häuschen im Grünen ist abbezahlt, doch die gesetzliche Rente reicht gerade so für das Nötigste. Als die Heizung im letzten Winter ausfiel, gerieten sie in Panik.
                      </p>
                      
                      <div className="bg-muted/50 p-4 md:p-5 rounded-lg">
                        <h4 className="font-semibold text-primary mb-2 text-base md:text-lg">Das Dilemma:</h4>
                        <p className="text-sm md:text-base text-muted-foreground">
                          „Wir wollen auf keinen Fall ausziehen", sagte Klaus. „Aber wir brauchen monatlich einfach 500 bis 800 Euro mehr, um gut zu leben und Rücklagen für Pflege oder Reparaturen zu haben."
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <h4 className="font-semibold text-primary text-base md:text-lg">Die Analyse:</h4>
                        <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                          <div className="bg-primary/5 p-3 md:p-4 rounded-lg">
                            <h5 className="font-medium text-primary mb-2 text-sm md:text-base">Leibrente</h5>
                            <p className="text-xs md:text-sm text-muted-foreground">Lebenslange Zahlung. Eine Wette auf ein langes Leben. Maximale Sicherheit für sie selbst.</p>
                          </div>
                          <div className="bg-accent/5 p-3 md:p-4 rounded-lg">
                            <h5 className="font-medium text-accent mb-2 text-sm md:text-base">Zeitrente</h5>
                            <p className="text-xs md:text-sm text-muted-foreground">Höhere Zahlungen über einen festen Zeitraum (z.B. 10 oder 15 Jahre). Vererbbar, falls ihnen etwas zustößt.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-accent/10 p-4 md:p-5 rounded-lg border-l-4 border-accent">
                        <h4 className="font-semibold text-primary mb-2 text-base md:text-lg">Die Entscheidung:</h4>
                        <p className="text-sm md:text-base text-muted-foreground mb-3">
                          Klaus und Renate entschieden sich für eine klassische <strong>Leibrente mit einer Mindestlaufzeitgarantie</strong>. So ist ihre monatliche Liquidität gesichert, egal wie alt sie werden (Langlebigkeitsrisiko abgedeckt), aber sollte ihnen in den ersten 10 Jahren etwas zustoßen, erhalten die Enkel den Restwert der Rentenzahlungen.
                        </p>
                      </div>
                      
                      <blockquote className="border-l-4 border-primary pl-4 md:pl-6 italic text-base md:text-lg text-primary">
                        „Seit die Rente monatlich kommt, schlafen wir wieder ruhig. Die neue Heizung ist bezahlt und wir planen die erste Kreuzfahrt unseres Lebens."
                        <footer className="text-sm text-muted-foreground mt-2 not-italic">– Renate</footer>
                      </blockquote>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Story 2: Die Rechte-Wahrer */}
        <section className="py-12 md:py-20 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-accent/10 p-3 rounded-xl">
                  <Home className="h-6 w-6 md:h-8 md:w-8 text-accent" />
                </div>
                <Badge variant="outline" className="text-sm md:text-base">Wohnrecht vs. Nießbrauch</Badge>
              </div>
              
              <Card className="card-elevated overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-5 gap-0">
                    <div className="lg:col-span-2 relative overflow-hidden">
                      <img 
                        src={storyMarianneGerd} 
                        alt="Marianne und Gerd - Paar am Küchentisch mit Bauplänen" 
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/40 to-transparent" />
                      <div className="relative p-6 md:p-8 flex flex-col justify-end h-full min-h-[280px]">
                        <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3 md:mb-4">
                          Marianne (74) und Gerd (76)
                        </h3>
                        <p className="text-lg md:text-xl text-accent font-semibold mb-4">
                          „Wir wollen die Kontrolle behalten."
                        </p>
                        <p className="text-sm md:text-base text-white/80">
                          Die „Rechte-Wahrer"
                        </p>
                      </div>
                    </div>
                    
                    <div className="lg:col-span-3 p-6 md:p-8 space-y-4 md:space-y-6">
                      <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                        Gerd war früher Architekt, Marianne Lehrerin. Ihr Haus ist groß, in bester Lage und sehr wertvoll. Sie benötigen eine größere Einmalzahlung (ca. 200.000 €), um ihre Kinder beim Hausbau zu unterstützen und das eigene Haus barrierefrei umzubauen.
                      </p>
                      
                      <div className="bg-muted/50 p-4 md:p-5 rounded-lg">
                        <h4 className="font-semibold text-primary mb-2 text-base md:text-lg">Das Dilemma:</h4>
                        <p className="text-sm md:text-base text-muted-foreground">
                          Ein monatlicher Kleckerbetrag hilft ihnen nicht. Sie brauchen Kapital jetzt. Gleichzeitig wollen sie die volle Kontrolle über ihre Immobilie behalten. Vielleicht wollen sie später, wenn das Haus zu groß wird, ausziehen und es vermieten?
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <h4 className="font-semibold text-primary text-base md:text-lg">Die Analyse:</h4>
                        <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                          <div className="bg-primary/5 p-3 md:p-4 rounded-lg">
                            <h5 className="font-medium text-primary mb-2 text-sm md:text-base">Wohnrecht</h5>
                            <p className="text-xs md:text-sm text-muted-foreground">Sie dürfen drin wohnen. Aber wenn sie ausziehen (z.B. ins Pflegeheim), verfällt das Recht oft oder lässt sich nicht wirtschaftlich nutzen.</p>
                          </div>
                          <div className="bg-accent/5 p-3 md:p-4 rounded-lg">
                            <h5 className="font-medium text-accent mb-2 text-sm md:text-base">Nießbrauch</h5>
                            <p className="text-xs md:text-sm text-muted-foreground">Das „stärkste" Recht. Sie verkaufen das Haus, bleiben aber „wirtschaftliche Eigentümer". Sie können wohnen, oder ausziehen und die Mieteinnahmen selbst kassieren!</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-accent/10 p-4 md:p-5 rounded-lg border-l-4 border-accent">
                        <h4 className="font-semibold text-primary mb-2 text-base md:text-lg">Die Entscheidung:</h4>
                        <p className="text-sm md:text-base text-muted-foreground">
                          Gerd und Marianne wählten den <strong>Verkauf mit lebenslangem Nießbrauchrecht</strong>. Sie haben die Option, das Haus zu vermieten, falls sie ins betreute Wohnen wechseln. Sie erhielten sofort eine hohe sechsstellige Summe und ließen sich das Nießbrauchrecht an erster Stelle im Grundbuch eintragen – maximale Insolvenzfestigkeit.
                        </p>
                      </div>
                      
                      <blockquote className="border-l-4 border-primary pl-4 md:pl-6 italic text-base md:text-lg text-primary">
                        „Das Nießbrauchrecht gibt uns das Gefühl, immer noch Herr im eigenen Haus zu sein. Niemand kann uns hier rauswerfen, und wir haben das Geld für den Umbau sofort bekommen."
                        <footer className="text-sm text-muted-foreground mt-2 not-italic">– Gerd</footer>
                      </blockquote>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Story 3: Die Jung-Senioren */}
        <section className="py-12 md:py-20">
          <div className="container-comfortable">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-accent/10 p-3 rounded-xl">
                  <Heart className="h-6 w-6 md:h-8 md:w-8 text-accent" />
                </div>
                <Badge variant="outline" className="text-sm md:text-base">Rückmietverkauf / Sale & Leaseback</Badge>
              </div>
              
              <Card className="card-elevated overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-5 gap-0">
                    <div className="lg:col-span-2 relative overflow-hidden">
                      <img 
                        src={storySabineThomas} 
                        alt="Sabine und Thomas - aktives Paar vor Wohnmobil" 
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/40 to-transparent" />
                      <div className="relative p-6 md:p-8 flex flex-col justify-end h-full min-h-[280px]">
                        <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3 md:mb-4">
                          Sabine (66) und Thomas (68)
                        </h3>
                        <p className="text-lg md:text-xl text-accent font-semibold mb-4">
                          „Zu jung für die Rente, zu alt für die Bank."
                        </p>
                        <p className="text-sm md:text-base text-white/80">
                          Die „Jung-Senioren"
                        </p>
                      </div>
                    </div>
                    
                    <div className="lg:col-span-3 p-6 md:p-8 space-y-4 md:space-y-6">
                      <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                        Beide sind noch keine 70. Sie fühlen sich fit und aktiv. Sie wollen reisen und das Leben genießen, solange es geht. Ihr Problem: Banken geben ihnen aufgrund des Alters und der Einkommenssituation keinen Kredit mehr, obwohl das Haus 600.000 € wert ist.
                      </p>
                      
                      <div className="bg-muted/50 p-4 md:p-5 rounded-lg">
                        <h4 className="font-semibold text-primary mb-2 text-base md:text-lg">Das Dilemma:</h4>
                        <p className="text-sm md:text-base text-muted-foreground">
                          Klassische Leibrenten-Modelle lohnen sich oft erst ab 70 oder 75 Jahren, da sonst die monatlichen Zahlungen zu gering sind (wegen der langen statistischen Restlebenserwartung).
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <h4 className="font-semibold text-primary text-base md:text-lg">Die Analyse:</h4>
                        <div className="bg-accent/5 p-3 md:p-4 rounded-lg">
                          <h5 className="font-medium text-accent mb-2 text-sm md:text-base">Rückmietverkauf</h5>
                          <ul className="text-xs md:text-sm text-muted-foreground space-y-1">
                            <li>• Das Haus wird zum vollen Marktpreis verkauft.</li>
                            <li>• Sie erhalten sofort 100% des Kapitals (minus eventueller Restschulden).</li>
                            <li>• Sie schließen einen normalen Mietvertrag ab und zahlen eine monatliche Miete.</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="bg-accent/10 p-4 md:p-5 rounded-lg border-l-4 border-accent">
                        <h4 className="font-semibold text-primary mb-2 text-base md:text-lg">Die Entscheidung:</h4>
                        <p className="text-sm md:text-base text-muted-foreground">
                          Sabine und Thomas verkauften ihr Haus an einen geprüften Investor. Sie bekamen den <strong>vollen Kaufpreis</strong> ausgezahlt. Einen Teil legten sie an, um die künftige Miete zu decken, mit dem Rest kauften sie das Wohnmobil und stockten ihre Reisekasse auf.
                        </p>
                      </div>
                      
                      <blockquote className="border-l-4 border-primary pl-4 md:pl-6 italic text-base md:text-lg text-primary">
                        „Wir haben das gebundene Beton-Gold in liquide Freiheit verwandelt. Dass wir jetzt Miete zahlen, stört uns nicht – wir haben ja das Geld dafür auf dem Konto und sind endlich flüssig."
                        <footer className="text-sm text-muted-foreground mt-2 not-italic">– Thomas</footer>
                      </blockquote>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Story 4: Die Unentschlossenen */}
        <section className="py-12 md:py-20 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-accent/10 p-3 rounded-xl">
                  <Users className="h-6 w-6 md:h-8 md:w-8 text-accent" />
                </div>
                <Badge variant="outline" className="text-sm md:text-base">Der Beratungsbedarf</Badge>
              </div>
              
              <Card className="card-elevated overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-5 gap-0">
                    <div className="lg:col-span-2 relative overflow-hidden">
                      <img 
                        src={storyHannelorePeter} 
                        alt="Hannelore und Peter - Paar beim Durchsehen von Dokumenten" 
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/40 to-transparent" />
                      <div className="relative p-6 md:p-8 flex flex-col justify-end h-full min-h-[280px]">
                        <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3 md:mb-4">
                          Hannelore (70) und Peter (72)
                        </h3>
                        <p className="text-lg md:text-xl text-accent font-semibold mb-4">
                          „Wir blicken im Dschungel der Angebote nicht mehr durch."
                        </p>
                        <p className="text-sm md:text-base text-white/80">
                          Die „Unentschlossenen"
                        </p>
                      </div>
                    </div>
                    
                    <div className="lg:col-span-3 p-6 md:p-8 space-y-4 md:space-y-6">
                      <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                        Hannelore und Peter wussten nur eines: So geht es nicht weiter. Die Rente ist knapp, das Haus ist da. Aber was ist das Richtige? Teilverkauf? Leibrente? Umkehrhypothek?
                      </p>
                      
                      <div className="bg-muted/50 p-4 md:p-5 rounded-lg">
                        <h4 className="font-semibold text-primary mb-2 text-base md:text-lg">Das Dilemma:</h4>
                        <p className="text-sm md:text-base text-muted-foreground">
                          Sie hatten Angst, über den Tisch gezogen zu werden. „Man hört so viel Schlechtes. Was ist das steuerlich? Was passiert bei Insolvenz des Käufers? Wer zahlt die Instandhaltung?"
                        </p>
                      </div>
                      
                      <div className="bg-primary/5 p-3 md:p-4 rounded-lg">
                        <h4 className="font-semibold text-primary mb-2 text-sm md:text-base">Der Weg zur Lösung:</h4>
                        <p className="text-xs md:text-sm text-muted-foreground">
                          Sie brauchten keinen Verkäufer, der ihnen ein Produkt aufschwatzt. Sie brauchten einen <strong>unabhängigen Vergleich</strong>. Ihre Situation (Alter, Immobilienwert, Gesundheitszustand, Kapitalbedarf) wurde analysiert und gegen alle verfügbaren Marktmodelle geprüft.
                        </p>
                      </div>
                      
                      <div className="bg-accent/10 p-4 md:p-5 rounded-lg border-l-4 border-accent">
                        <h4 className="font-semibold text-primary mb-2 text-base md:text-lg">Das Ergebnis:</h4>
                        <p className="text-sm md:text-base text-muted-foreground">
                          Für Hannelore und Peter war ein <strong>Teilverkauf</strong> die beste Lösung. Sie verkauften nur 50% ihres Hauses, erhielten sofort Bargeld, zahlten ein geringes Nutzungsentgelt für die verkaufte Hälfte und partizipieren weiterhin an der Wertsteigerung der Immobilie.
                        </p>
                      </div>
                      
                      <blockquote className="border-l-4 border-primary pl-4 md:pl-6 italic text-base md:text-lg text-primary">
                        „Ohne den Vergleich hätten wir vielleicht das Falsche gemacht. Die kostenlose Beratung hat uns die Augen geöffnet."
                        <footer className="text-sm text-muted-foreground mt-2 not-italic">– Hannelore</footer>
                      </blockquote>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Social Proof & Authority Section */}
        <section className="py-12 md:py-20 bg-primary text-primary-foreground">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8">
                Warum Sie nicht alleine entscheiden sollten
              </h2>
              
              <p className="text-base md:text-lg lg:text-xl opacity-90 mb-8 md:mb-12 max-w-3xl mx-auto">
                Die Geschichten von Renate, Klaus, Marianne, Gerd, Sabine, Thomas, Hannelore und Peter sind keine Einzelfälle. Sie stehen stellvertretend für eine ganze Generation.
              </p>
              
              <div className="bg-primary-foreground/10 p-6 md:p-8 rounded-2xl mb-8 md:mb-12">
                <p className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2">Über 10.000</p>
                <p className="text-base md:text-lg opacity-90">Eigentümer haben bereits den Schritt gewagt und sich kostenlos beraten lassen.</p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 text-left">
                <div className="bg-primary-foreground/10 p-4 md:p-6 rounded-xl">
                  <CheckCircle className="h-6 w-6 md:h-8 md:w-8 mb-3 text-accent" />
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Marktüberblick</h3>
                  <p className="text-xs md:text-sm opacity-80">Wir vergleichen Leibrente, Zeitrente, Nießbrauch, Wohnrecht, Rückmietverkauf und Teilverkauf.</p>
                </div>
                <div className="bg-primary-foreground/10 p-4 md:p-6 rounded-xl">
                  <CheckCircle className="h-6 w-6 md:h-8 md:w-8 mb-3 text-accent" />
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Unabhängigkeit</h3>
                  <p className="text-xs md:text-sm opacity-80">Wir arbeiten nicht für einen Anbieter, sondern suchen die beste Lösung für Sie.</p>
                </div>
                <div className="bg-primary-foreground/10 p-4 md:p-6 rounded-xl">
                  <CheckCircle className="h-6 w-6 md:h-8 md:w-8 mb-3 text-accent" />
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Sicherheit</h3>
                  <p className="text-xs md:text-sm opacity-80">Nur geprüfte, bonitätsstarke Investoren und vertraglich gesicherte Rechte (Grundbucheintrag).</p>
                </div>
                <div className="bg-primary-foreground/10 p-4 md:p-6 rounded-xl">
                  <CheckCircle className="h-6 w-6 md:h-8 md:w-8 mb-3 text-accent" />
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Alles aus einer Hand</h3>
                  <p className="text-xs md:text-sm opacity-80">Von der Wertermittlung bis zum Notartermin.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-20">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-8 md:mb-12 text-center">
                Häufig gestellte Fragen
              </h2>
              
              <Accordion type="single" collapsible className="space-y-3 md:space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-xl border px-4 md:px-6">
                    <AccordionTrigger className="text-left text-base md:text-lg font-medium py-4 md:py-5">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-muted-foreground pb-4 md:pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-accent/10 via-background to-primary/5">
          <div className="container-comfortable">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-4 md:mb-6">
                Welches Modell passt zu Ihrer Geschichte?
              </h2>
              
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground mb-6 md:mb-8">
                Finden Sie es in weniger als 2 Minuten heraus. Kostenlos und unverbindlich.
              </p>
              
              <div className="flex flex-col items-center gap-4">
                <Button 
                  size="lg" 
                  className="text-lg md:text-xl px-8 md:px-12 py-5 md:py-6"
                  onClick={() => window.location.href = '/#advisor-wizard'}
                >
                  <Calculator className="mr-2 h-5 w-5 md:h-6 md:w-6" />
                  Jetzt kostenlose Analyse starten
                  <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                </Button>

                <button 
                  className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors cursor-pointer whitespace-nowrap text-base md:text-lg font-medium"
                  onClick={() => window.open('tel:089628246446', '_self')}
                >
                  <Phone className="h-4 w-4 md:h-5 md:w-5" />
                  <span>089 62 82 46 46</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <ContactSection />
        <HeyflowBanner />
      </main>
    </div>
  );
};

export default Erfolgsgeschichten;
